import React, { useContext, useEffect, useState } from 'react';
import { Button, CircularProgress, Box, Dialog, Typography, TableHead, Tab, TableCell, Table, TableBody, TableRow, TablePagination, TextField, Checkbox, Grid2, FormControl, InputLabel, Select, MenuItem, Tooltip } from '@mui/material';
import { fetchReports, Report } from '../../../services/stixify_api.ts';
import { Link, useLocation } from 'react-router-dom';
import { TeamContext } from '../../../contexts/team-context.tsx';
import { getDateString, updateURLWithParams } from '../../../services/utils.ts';
import { URLS } from '../../../services/urls.ts';
import { TeamRouteContext } from '../../team-layout.tsx/index.tsx';

const PAGE_SIZE = 50

const Reports: React.FC = () => {
  const [data, setData] = useState<Report[]>([]);
  const [loading, setLoading] = useState<boolean>(false); // Loading state
  const [page, setPage] = useState<number>(0); // Current page
  const [pageSize, setPageSize] = useState<number>(10); // Profiles per page
  const [dataCount, setDataCount] = useState<number>(0); // Total number of pages
  const { teamId } = useContext(TeamRouteContext);
  const { activeTeam } = useContext(TeamContext);
  const [filter, setFilter] = useState<{
    name: string,
    description: string,
    created_by_ref: string,
    profile_id: string,
    created_at_after: string,
    created_at_before: string,
    updated_at_after: string,
    updated_at_before: string,
    show_only_my_team_reports: boolean,
    tlp_levels: string[]
  }>({
    name: '',
    description: '',
    created_by_ref: '',
    profile_id: '',
    created_at_after: '',
    created_at_before: '',
    updated_at_after: '',
    updated_at_before: '',
    show_only_my_team_reports: false,
    tlp_levels: []
  })
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sortField, setSortField] = useState<string>('created_at');
  const [initialDataLoaded, setInitialDataLoaded] = useState(0)
  const location = useLocation();

  const loadData = async (pageNumber: number) => {
    if (!initialDataLoaded) return
    const queryFilter = { ...filter, tlp_levels: filter.tlp_levels.join(',') }
    updateURLWithParams({ ...queryFilter, page: pageNumber, sortField, sortOrder})
    setLoading(true);
    const res = await fetchReports(teamId, queryFilter, sortField, sortOrder, pageNumber + 1);
    setData(res.data.results)
    setDataCount(res.data.count);
    setPageSize(10)
    setLoading(false);
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    setPage(Number(query.get('page') || 0))
    setSortField(query.get('sortField') || 'created_at')
    setSortOrder(query.get('sortOrder') === 'asc' ? 'asc' : 'desc')
    setFilter({
      name: query.get('name') || '',
      description: query.get('description') || '',
      created_by_ref: query.get('created_by_ref') || '',
      profile_id: query.get('profile_id') || '',
      created_at_after: query.get('created_at_after') || '',
      created_at_before: query.get('created_at_before') || '',
      updated_at_after: query.get('updated_at_after') || '',
      updated_at_before: query.get('updated_at_before') || '',
      show_only_my_team_reports: query.get('show_only_my_team_reports') == 'true',
      tlp_levels: query.get('tlp_levels')?.split(',') || ([] as string[]),
    })
    setInitialDataLoaded(1)
  }, [location])

  useEffect(() => {
    if (initialDataLoaded) loadData(page)
  }, [initialDataLoaded])

  // Load data on initial render and when the page changes
  useEffect(() => { loadData(page); }, [page, teamId]);

  const handlePageChange = (event: unknown, value: number) => {
    setPage(value); // Set new page number
  };

  const getReportDetailUrl = (report: Report) => {
    if (teamId) {
      return URLS.teamReportDetail(teamId, report.id)
    }
    return URLS.staffReportDetail(report.id)
  }

  useEffect(() => {
    document.title = 'Reports | Stixify Web'
  }, [])

  const setFilterField = (fieldName: string, value: string) => {
    setFilter((filer) => ({ ...filter, [fieldName]: value }))
  }

  const setFilterFieldList = (fieldName: string, value: any) => {
    setFilter((filer) => ({ ...filter, [fieldName]: value }))
  }

  const handleSortChange = (field: string) => {
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);
    setPage(0);
    setInitialDataLoaded(initialDataLoaded + 1)
  };

  const applyFilter = () => {
    loadData(0)
  }

  const canUploadReport = () => {
    if(!teamId) return false
    return (activeTeam?.allowed_reports_per_month || 0) > (activeTeam?.month_upload_count || 0)
  }
  const getJobsUrl = () => {
    if(teamId) {
      return URLS.teamJobs(teamId)
    }
    else {
      return URLS.staffJobs()
    }
  }

  return (
    <Box p={4}>
      <Box sx={{ display: 'flex', marginBottom: '2rem' }}>
        <Typography variant='h4'>Reports</Typography>
        {teamId && <>
          {canUploadReport() ? <Link to={URLS.teamAddReport(teamId || '')}>
            <Button
              sx={{ marginLeft: '5rem', textTransform: 'uppercase' }} variant="contained" color="success">
              Upload A New Report
            </Button>
          </Link> : <>
            <Tooltip title='Upload limits of your plan exceeded. Please upgrade'>
              <div>
                <Button
                  disabled={true}
                  sx={{ marginLeft: '5rem', textTransform: 'uppercase' }} variant="contained" color="success">
                  Upload A New Report
                </Button>
              </div>
            </Tooltip>
          </>}
        </>}
        <Link to={getJobsUrl()}>
          <Button sx={{ marginLeft: '5rem', textTransform: 'uppercase' }} variant="contained">
            Track Upload Status
          </Button>
        </Link>
      </Box>
      <Typography>
        <p>Search through reports uploaded to Stixify. The search will return all Reports marked TLP:Green, and all Reports belonging to your team.</p>
      </Typography>

      <Box>
        <Grid2 container spacing={2}>
          <Grid2 size={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', }}>
              <TextField
                label="Report Name"
                variant="outlined"
                value={filter.name} onChange={(ev) => setFilterField('name', ev.target.value)}>
              </TextField>
            </Box>
          </Grid2>
          <Grid2 size={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', }}>
              <TextField label="Created By" value={filter.created_by_ref} onChange={(ev) => setFilterField('created_by_ref', ev.target.value)}></TextField>
            </Box>
          </Grid2>
          <Grid2 size={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', }}>
              <TextField label="Profile ID" value={filter.profile_id} onChange={(ev) => setFilterField('profile_id', ev.target.value)}></TextField>
            </Box>
          </Grid2>
          <Grid2 size={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', }}>
              <TextField
                type='date'
                label="Earliest Created Time"
                value={filter.created_at_after}
                onChange={(ev) => setFilterField('created_at_after', ev.target.value)}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}></TextField>
            </Box>
          </Grid2>
          <Grid2 size={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', }}>
              <TextField
                type='date'
                label="Latest Created Time"
                value={filter.created_at_before}
                onChange={(ev) => setFilterField('created_at_before', ev.target.value)}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              ></TextField>
            </Box>
          </Grid2>
          <Grid2 size={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', }}>
              <FormControl>
                <InputLabel id="tlp_levels">TLP Level(s)</InputLabel>
                <Select
                  multiple
                  value={filter.tlp_levels} onChange={(ev) => setFilterFieldList('tlp_levels', ev.target.value)}
                  labelId="tlp_levels">
                  <MenuItem value='amber'>Amber</MenuItem>
                  <MenuItem value='green'>Green</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid2>
          {teamId && <Grid2 size={4}>
            <Checkbox
              checked={filter.show_only_my_team_reports}
              onChange={() => setFilterField('show_only_my_team_reports', !filter.show_only_my_team_reports)}
            />
            Only show reports for my team
          </Grid2>}
          <Grid2 size={4}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Button onClick={applyFilter} variant='contained' >Filter</Button>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Table>
        <TableHead>
          <TableCell onClick={() => handleSortChange('name')} style={{ cursor: 'pointer' }}>
            Report Title
            {sortField === 'name' ? (sortOrder === 'asc' ?  '↓' : '↑') : ''}
          </TableCell>
          {teamId && <TableCell>Owned by this Team</TableCell>}
          <TableCell onClick={() => handleSortChange('created_at')} style={{ cursor: 'pointer' }}>
            Report created
            {sortField === 'created_at' ? (sortOrder === 'asc' ? '↓' : '↑') : ''}
          </TableCell>
          <TableCell onClick={() => handleSortChange('tlp_level')} style={{ cursor: 'pointer' }}>
            TLP Level
            {sortField === 'tlp_level' ? (sortOrder === 'asc' ? '↓' : '↑') : ''}
          </TableCell>
          <TableCell onClick={() => handleSortChange('confidence')} style={{ cursor: 'pointer' }}>
            Confidence score
            {sortField === 'confidence' ? (sortOrder === 'asc' ? '↓' : '↑') : ''}
          </TableCell>
          <TableCell>Created By </TableCell>
          <TableCell>Labels</TableCell>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={6}>
                <CircularProgress style={{ display: 'block', margin: '20px auto' }} />
              </TableCell>
            </TableRow>
          ) : (<>
            {data.map((report) => (
              <TableRow key={report.id}>
                <TableCell>
                  <Link to={getReportDetailUrl(report)}>{report.stixify_file_metadata.name}</Link></TableCell>
                {teamId && <TableCell>{report.team_id === teamId ? 'True' : 'False'}</TableCell>}
                <TableCell>{getDateString(report?.created_at || '')}</TableCell>
                <TableCell>
                  {report.stixify_file_metadata.tlp_level}
                </TableCell>
                <TableCell>
                  {report.stixify_file_metadata.confidence}
                </TableCell>
                <TableCell>{report.stixify_file_metadata.identity.id}</TableCell>
                <TableCell>{report.stixify_file_metadata.labels?.join(',')}</TableCell>
              </TableRow>
            ))}
          </>
          )}
        </TableBody>
      </Table>

      {/* Pagination controls */}
      <TablePagination
        component="div"
        count={dataCount}
        page={page}
        rowsPerPage={pageSize}
        onPageChange={handlePageChange}
        color="primary"
        rowsPerPageOptions={[]}
        labelRowsPerPage={""} // Hide the rows per page label
        style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}
      />
    </Box>
  );
};

export default Reports;
